import { MessageAction } from "@accurx/api/apps";
import { useAppsContext, useExecuteMessageActionMutation } from "@accurx/apps";
import { useCurrentWorkspace } from "@accurx/auth";
import { ConversationItem } from "@accurx/concierge/schemas/ConversationItemSchema";
import { Conversation } from "@accurx/concierge/schemas/ConversationSchema";
import { Ds, Feedback } from "@accurx/design";
import { ContentTypesWithSaveToRecordSupport } from "@accurx/native";
import { useIsMutating } from "@tanstack/react-query";
import { SaveItemAction } from "domains/inbox/components/ConversationItem/components/SaveItemAction/SaveItemAction";
import { toast } from "react-toastify";

type MessageActionsProps = {
    item: Extract<
        ConversationItem,
        { contentType: ContentTypesWithSaveToRecordSupport }
    >;
    conversation: Conversation;
    hasAttachments?: boolean;
    getTextToCopy: () => string;
};

const useExecuteAction = (dependencies: readonly unknown[]) => {
    const mutationKey = ["ExecuteMessageAction", ...dependencies];

    const mutation = useExecuteMessageActionMutation({
        mutationKey: mutationKey,
        onSuccess: (data, { action }) => {
            toast(
                Feedback({
                    colour: "success",
                    title: `${action.name} - succeeded`,
                }),
            );
        },
        onError: (error, { action }) => {
            toast(
                Feedback({ colour: "error", title: `${action.name} - failed` }),
            );
        },
    });

    return {
        executeAction: mutation.mutate,
        inProgress: useIsMutating(mutationKey) > 0,
    };
};

const ActionButton = ({
    action,
    item,
    workspaceId,
}: {
    action: MessageAction;
    item: ConversationItem;
    workspaceId: number;
}) => {
    const { executeAction, inProgress } = useExecuteAction([
        action.id,
        workspaceId,
        item.id,
    ]);

    return (
        <Ds.Button
            key={action.id}
            appearance="secondary"
            size="small"
            onClick={() =>
                executeAction({
                    workspaceId,
                    messageId: item.id,
                    action,
                })
            }
            disabled={inProgress}
        >
            {action.name}
        </Ds.Button>
    );
};

const ActionDropdownItem = ({
    action,
    item,
    workspaceId,
}: {
    action: MessageAction;
    item: ConversationItem;
    workspaceId: number;
}) => {
    const { executeAction, inProgress } = useExecuteAction([
        action.id,
        workspaceId,
        item.id,
    ]);

    return (
        <Ds.DropdownMenu.Item
            onSelect={() =>
                executeAction({
                    workspaceId,
                    messageId: item.id,
                    action,
                })
            }
            disabled={inProgress}
        >
            {action.name}
        </Ds.DropdownMenu.Item>
    );
};

const AppsMessageActions = ({ item }: { item: ConversationItem }) => {
    const { messageActions, isLoading, isError } = useAppsContext();
    const { orgId: workspaceId } = useCurrentWorkspace();

    if (isLoading) {
        // Message actions should always be loaded by the time we get here but it's helpful for tests
        return <div role="status" />;
    }

    if (isError || messageActions.length === 0) {
        return null;
    }

    const [firstAction, ...otherActions] = messageActions;

    if (otherActions.length === 0) {
        return (
            <ActionButton
                action={firstAction}
                item={item}
                workspaceId={workspaceId}
            />
        );
    }

    return (
        <Ds.Flex gap="1">
            <ActionButton
                action={firstAction}
                item={item}
                workspaceId={workspaceId}
            />
            {otherActions.length === 1 ? (
                <ActionButton
                    action={otherActions[0]}
                    item={item}
                    workspaceId={workspaceId}
                />
            ) : (
                <Ds.DropdownMenu modal>
                    <Ds.DropdownMenu.Trigger asChild>
                        <Ds.Button
                            appearance="secondary"
                            size="small"
                            aria-label="More actions"
                            title="More actions"
                        >
                            <Ds.Button.Icon name="More" appearance="solid" />
                        </Ds.Button>
                    </Ds.DropdownMenu.Trigger>
                    <Ds.DropdownMenu.Content size="xsmall" align="end">
                        {otherActions.map((action) => (
                            <ActionDropdownItem
                                key={action.id}
                                action={action}
                                item={item}
                                workspaceId={workspaceId}
                            />
                        ))}
                    </Ds.DropdownMenu.Content>
                </Ds.DropdownMenu>
            )}
        </Ds.Flex>
    );
};

export const MessageActions = ({
    item,
    conversation,
    hasAttachments,
    getTextToCopy,
}: MessageActionsProps) => {
    return (
        <Ds.Flex gap="1">
            <SaveItemAction
                item={item}
                conversation={conversation}
                getTextToCopy={getTextToCopy}
                hasAttachments={hasAttachments}
            />
            <AppsMessageActions item={item} />
        </Ds.Flex>
    );
};
